<template>
  <lf-dropdown
    ref="dropdownRef"
    :options="options"
    :value="activeOption"
    :disabled="disabled"
    name="note-type"
    class="note-type-selector h-6 w-35 justify-center rounded border"
    data-cy="note-type"
  >
    <template #toggle-button="{ toggle, value, menuOpen }">
      <div
        class="flex cursor-pointer items-center justify-between rounded p-1 px-2"
        @click.stop="toggle"
      >
        <span class="text-headline-new">
          {{ upperFirst(value.toString()) }}
        </span>
        <icon-base
          height="6"
          width="10"
          :icon="IconDropdownArrow"
          class="text-headline-new"
          :class="{ 'rotate-180 transform': menuOpen }"
        />
      </div>
    </template>
    <template #listItem="{ optionKey, option }">
      <div
        class="flex cursor-pointer items-center justify-between rounded p-2 hover:bg-primary-background-active"
        data-cy="note-type-option"
        @click="selectType(optionKey)"
      >
        <div class="flex items-center space-x-2">
          <icon-base :icon="getIcon(optionKey as string)" />
          <div class="text-headline">{{ option }}</div>
        </div>
        <icon-base
          v-if="optionKey === activeOption"
          :icon="IconCheck"
          class="text-primary"
        />
      </div>
    </template>
  </lf-dropdown>
</template>

<script setup lang="ts">
import { ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import upperFirst from "lodash/upperFirst";

import { NoteType } from "@/enums/dealActivity";

import IconBusiness from "@/components/icons/IconBusiness.vue";
import IconCheck from "@/components/icons/IconCheck.vue";
import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconNotes from "@/components/icons/IconNotes.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";

const emit = defineEmits<{
  "type:update": [NoteType];
}>();

const { noteType } = defineProps<{
  noteType: NoteType;
  disabled?: boolean;
}>();

const { t } = useI18n();

const activeOption = ref(noteType);
const dropdownRef = useTemplateRef("dropdownRef");

const noteTypes = [
  {
    title: t("DEALS.NOTES.DEAL_NOTE"),
    icon: IconNotes,
    type: NoteType.Application,
  },
  {
    title: t("DEALS.NOTES.BUSINESS_NOTE"),
    icon: IconBusiness,
    type: NoteType.Business,
  },
  // TODO: uncomment when we can manually add call notes
  // {
  //   title: t("DEALS.NOTES.CALL_NOTE"),
  //   icon: IconTelephone,
  //   type: NoteType.Call,
  // },
];

const options = noteTypes.reduce((acc: Record<string, string>, { type }) => {
  acc[type] = upperFirst(type);
  return acc;
}, {});

const getIcon = (type: string) => {
  return noteTypes.find((noteType) => noteType.type === type)?.icon ?? "";
};

const selectType = (type: unknown) => {
  const selectedType = type as NoteType;
  activeOption.value = selectedType;
  emit("type:update", selectedType);
  dropdownRef.value?.toggleMenu();
};
</script>
<style>
@reference "@/assets/tailwind.css";

.note-type-selector .dropdown {
  @apply p-2;
}
</style>
